import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { useSettings } from '../../context/SettingsContext';
import '../css/Category.css';

const BundleProductCreate = () => {
  const navigate = useNavigate();
  const { currencySymbol } = useSettings();
  const [form, setForm] = useState({
    CategoryId: '',
    BrandId: '',
    ProductName: '',
    SKU: '',
    Price: '0.00',
    ShortDescription: '',
    FullDescription: '',
    Material: '',
    Gender: '',
    IsFeatured: false,
    IsTrending: false,
    IsNewArrival: false,
    IsActive: true,
  });
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    apiFetch('/BundleProduct/GetCategoryDropdown')
      .then(({ data }) => setCategories(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setCategories([]));
    apiFetch('/BundleProduct/GetBrandDropdown')
      .then(({ data }) => setBrands(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setBrands([]));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.ProductName.trim()) { setFormError('Product Name is required.'); return; }
    if (!form.CategoryId) { setFormError('Please select a Category.'); return; }
    if (!form.SKU.trim()) { setFormError('SKU is required.'); return; }

    setSaving(true);
    try {
      const { data } = await apiFetch('/BundleProduct/Create', {
        method: 'POST',
        data: {
          CategoryId: Number(form.CategoryId),
          BrandId: form.BrandId ? Number(form.BrandId) : 0,
          ProductName: form.ProductName,
          SKU: form.SKU,
          Price: parseFloat(form.Price) || 0,
          ShortDescription: form.ShortDescription,
          FullDescription: form.FullDescription,
          Material: form.Material,
          Gender: form.Gender,
          IsFeatured: form.IsFeatured,
          IsTrending: form.IsTrending,
          IsNewArrival: form.IsNewArrival,
          IsActive: form.IsActive,
          CreatedBy: 0,
        },
      });

      let newId =
        data?.data?.BundleProductId ?? data?.data?.bundleProductId ??
        data?.BundleProductId ?? data?.bundleProductId;

      if (newId) {
        navigate(`/admin/bundleproduct/edit/${newId}`, {
          state: { toast: 'Bundle product added successfully.', tab: 'images' },
        });
      } else {
        navigate('/admin/bundleproduct', { state: { toast: 'Bundle product created successfully.' } });
      }
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <AdminLink to="/admin/bundleproduct">Bundle Products</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Add Bundle Product</span>
      </p>

      <div className="row">
        <div className="col-lg-12 col-md-10 col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-1">Add Bundle Product</h4>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>Fill in bundle product details</p>

              {formError && (
                <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                  <i className="mdi mdi-alert-circle me-1"></i>{formError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Product Name <span className="text-danger">*</span></label>
                      <input className="form-control" name="ProductName" value={form.ProductName} onChange={handleChange} placeholder="Product Name" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">SKU <span className="text-danger">*</span></label>
                      <input className="form-control" name="SKU" value={form.SKU} onChange={handleChange} placeholder="SKU" />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Price</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', pointerEvents: 'none', fontSize: '0.9rem', zIndex: 1 }}>{currencySymbol}</span>
                        <input
                          className="form-control"
                          style={{ paddingLeft: 24 }}
                          type="text"
                          inputMode="decimal"
                          name="Price"
                          value={form.Price}
                          onChange={handleChange}
                          onFocus={e => e.target.select()}
                          onBlur={e => {
                            const val = parseFloat(e.target.value);
                            setForm(prev => ({ ...prev, Price: isNaN(val) ? '0.00' : val.toFixed(2) }));
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Category <span className="text-danger">*</span></label>
                      <select className="form-control" name="CategoryId" value={form.CategoryId} onChange={handleChange}>
                        <option value="">— Select Category —</option>
                        {categories.map(c => (
                          <option key={c.CategoryId} value={c.CategoryId}>{c.CategoryName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Brand</label>
                      <select className="form-control" name="BrandId" value={form.BrandId} onChange={handleChange}>
                        <option value="">— Select Brand —</option>
                        {brands.map(b => (
                          <option key={b.BrandId} value={b.BrandId}>{b.BrandName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Short Description</label>
                      <input className="form-control" name="ShortDescription" value={form.ShortDescription} onChange={handleChange} placeholder="Short Description" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Material</label>
                      <input className="form-control" name="Material" value={form.Material} onChange={handleChange} placeholder="Material" />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Gender</label>
                      <input className="form-control" name="Gender" value={form.Gender} onChange={handleChange} placeholder="Gender" />
                    </div>
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">Full Description</label>
                  <textarea className="form-control" name="FullDescription" value={form.FullDescription} onChange={handleChange} placeholder="Full Description" rows={4} />
                </div>

                <div className="d-flex flex-wrap gap-3 mb-4">
                  {[
                    { name: 'IsActive', label: 'Active' },
                  ].map(({ name, label }) => (
                    <div className="form-check" key={name}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name={name}
                        id={`create_${name}`}
                        checked={form[name]}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor={`create_${name}`}>{label}</label>
                    </div>
                  ))}
                </div>

                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving...</> : 'Submit'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/bundleproduct')}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BundleProductCreate;
